import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useVoiceCreditPlans } from "../../hooks/useVoiceCreditPlans";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Link,
  Divider,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Badge,
  Button,
  Icon,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaCheck, FaBolt, FaRocket } from "react-icons/fa";
import { TiltCard } from "../common/TiltCard";
import { usePublicGlassSurface } from "../common/publicVisual";
import { gtagReportConversionThenNavigate } from "../../utils/googleAdsPageViewConversion";
import PageSeo from "../../seo/PageSeo";
import { DEFAULT_KEYWORDS, SITE_URL } from "../../seo/siteConfig";
import { breadcrumbSchema } from "../../seo/structuredData";

import {
  formatPlanCredits,
  formatPlanInr,
  planCreditsPerRupee,
} from "../../utils/aiCreditPlans";

function PlanCard({
  eyebrow,
  title,
  priceLine,
  body,
  bullets,
  icon,
  highlight,
}) {
  const glass = usePublicGlassSurface();
  const border = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const liftShadow = useColorModeValue(
    "0 18px 50px -24px rgba(45, 212, 191, 0.35)",
    "0 22px 55px -20px rgba(45, 212, 191, 0.2)"
  );

  return (
    <TiltCard
      tilt={9}
      w="full"
      h="full"
      borderRadius="2xl"
      borderWidth={highlight ? "2px" : "1px"}
      borderColor={highlight ? "teal.400" : border}
      minH={{ md: "320px" }}
      position="relative"
      p={{ base: 6, md: 7 }}
      display="flex"
      flexDirection="column"
      _hover={
        highlight
          ? {
              boxShadow: liftShadow,
            }
          : undefined
      }
      sx={{ transformStyle: "preserve-3d" }}
      {...glass}
    >
      <VStack align="stretch" spacing={5} flex={1}>
        <Badge
        alignSelf="flex-start"
        colorScheme={highlight ? "teal" : "gray"}
        px={3}
        py={0.5}
        borderRadius="full"
      >
        {eyebrow}
        </Badge>
        <Heading size="lg" fontWeight="extrabold">
          <Box as="span" display="inline-flex" alignItems="center" gap={2}>
            <Icon as={icon} mt={1} />
            {title}
          </Box>
        </Heading>
        <Text fontSize="2xl" fontWeight="bold" color={highlight ? "teal.500" : "inherit"}>
          {priceLine}
        </Text>
        <Text fontSize="sm" color={muted} lineHeight="tall">
          {body}
        </Text>
        <List spacing={3} flex={1}>
          {bullets.map((b) => (
            <ListItem key={b} fontSize="sm" color={muted} display="flex" alignItems="flex-start">
              <ListIcon as={FaCheck} color="teal.500" mt={1} />
              {b}
            </ListItem>
          ))}
        </List>
      </VStack>
    </TiltCard>
  );
}

const VOICE_ANCHOR_ID = "voice";

function scrollToVoiceSection() {
  document.getElementById(VOICE_ANCHOR_ID)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

const Pricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { plans: voicePlans } = useVoiceCreditPlans(true);
  const muted = useColorModeValue("gray.600", "gray.400");
  const deep = useColorModeValue("gray.900", "gray.50");
  const tableGlass = usePublicGlassSurface();

  /** React Router does not scroll to `#voice` reliably—handle hash + in-page jump explicitly. */
  useEffect(() => {
    const normalized = (location.hash || "").replace(/^#/, "");
    if (normalized !== VOICE_ANCHOR_ID) return undefined;
    const t = window.setTimeout(() => scrollToVoiceSection(), 0);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  const jumpToVoicePacks = () => {
    if (location.pathname !== "/pricing") {
      navigate({ pathname: "/pricing", hash: VOICE_ANCHOR_ID });
      return;
    }
    scrollToVoiceSection();
  };

  const jsonLd = breadcrumbSchema([
    { name: "Home", url: SITE_URL },
    { name: "AI Usage Credits pricing", url: `${SITE_URL}/pricing` },
  ]);

  return (
    <Box as="main" bg="transparent" py={{ base: 10, md: 16 }} minH="70vh">
      <PageSeo
        title="AI Usage Credits pricing — Screen AI, Media AI, voice"
        description="Prepaid AI Usage Credit packs for Screen AI, Media AI, Portfolio AI, Project AI, Todos AI, and Daily Chore AI. Core dashboard stays free."
        keywords={`${DEFAULT_KEYWORDS}, AI credits pricing, Razorpay credits India`}
        canonicalPath="/pricing"
        jsonLd={jsonLd}
      />
      <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
        <VStack spacing={4} align="stretch" mb={12} maxW="2xl">
          <Heading as="h1" size="2xl" color={deep}>
            Pricing
          </Heading>
          <Text color={muted} fontSize="md" lineHeight="tall">
            The app is <strong>free</strong>. You can buy <strong>AI Usage Credits</strong> in rupees (Razorpay) for Screen AI, Media AI, and the voice helper on supported screens.
          </Text>
          <PricingHeroActions onJumpToPacks={jumpToVoicePacks} />
        </VStack>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 6, lg: 8 }} mb={14} maxW="1000px" mx="auto">
          <PlanCard
            eyebrow="Included"
            title="Dashboard"
            priceLine="₹0"
            body="Portfolio AI, Project AI, Todos AI, Daily Chore AI, notes, files, calendar, org tree, and group bills—after you sign up."
            icon={FaBolt}
            bullets={["One sidebar for all tools", "No monthly fee for the workspace"]}
          />
          <PlanCard
            eyebrow="Optional"
            title="AI Usage Credits"
            priceLine="Pay in ₹"
            body="Prepaid AI Usage Credits for Screen AI, Media AI, and voice on Portfolio AI, Project AI, Todos AI, and Daily Chore AI."
            icon={FaRocket}
            highlight
            bullets={["Cards or UPI via Razorpay", "AI Usage Credits stay on your account until used"]}
          />
        </SimpleGrid>

        <Box id={VOICE_ANCHOR_ID} mb={14} scrollMarginTop="80px">
          <Heading as="h2" size="md" mb={2}>
            AI Usage Credit packs (Indian rupees)
          </Heading>
          <Text fontSize="sm" color={muted} mb={6} lineHeight="tall">
            Prices also appear in the app when you buy AI Usage Credits.
          </Text>
          <TableContainer
            borderRadius="xl"
            borderWidth="1px"
            overflow="hidden"
            {...tableGlass}
          >
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Pack</Th>
                  <Th isNumeric>Price (INR)</Th>
                  <Th isNumeric>AI Usage Credits</Th>
                  <Th isNumeric>Credits per ₹</Th>
                </Tr>
              </Thead>
              <Tbody>
                {voicePlans.map((row) => (
                  <Tr key={row.name}>
                    <Td fontWeight="medium">{row.name}</Td>
                    <Td isNumeric>{formatPlanInr(row.amountInRupees)}</Td>
                    <Td isNumeric>{formatPlanCredits(row.tokenCredits)}</Td>
                    <Td isNumeric color={muted}>
                      {Math.round(planCreditsPerRupee(row)).toLocaleString("en-IN")}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        <VStack align="stretch" spacing={3}>
          <Text fontSize="sm" color={muted}>
            <Link as={RouterLink} to="/contact-us" color="teal.500" fontWeight="semibold">
              Contact
            </Link>
            {" · "}
            <Link as={RouterLink} to="/faqs" color="teal.500" fontWeight="semibold">
              FAQs
            </Link>
          </Text>
          <Divider />
          <Text fontSize="xs" color={muted}>
            <Link as={RouterLink} to="/login" color="teal.500">
              Log in
            </Link>{" "}
            to buy AI Usage Credit packs from the app.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

function PricingHeroActions({ onJumpToPacks }) {
  const navigate = useNavigate();
  return (
    <Box>
      <Button
        mr={3}
        mb={3}
        colorScheme="teal"
        size="lg"
        type="button"
        onClick={() => gtagReportConversionThenNavigate(navigate, "/register")}
      >
        Create free account
      </Button>
      <Button mb={3} variant="outline" colorScheme="teal" size="lg" type="button" onClick={onJumpToPacks}>
        AI Usage Credit pack list
      </Button>
    </Box>
  );
}

export default Pricing;
